import api from '../utils/api';

/** StableDocs API (backend/src/modules/stabledocs). Shapes mirror the controller and DTOs. */

export const PURPOSES = ['referral', 'owner_copy', 'pre_purchase', 'insurance', 'transfer_of_care'] as const;
export type Purpose = (typeof PURPOSES)[number];

export const PURPOSE_LABELS: Record<Purpose, string> = {
  referral: 'Referral',
  owner_copy: 'Owner copy',
  pre_purchase: 'Pre-purchase',
  insurance: 'Insurance',
  transfer_of_care: 'Transfer of care',
};

/** In the order their sections print. */
export const RECORD_TYPES = [
  'soap',
  'clinical_notes',
  'lab_results',
  'procedures',
  'vaccinations',
  'medications',
  'imaging',
  'coggins',
  'attachments',
] as const;
export type RecordType = (typeof RECORD_TYPES)[number];

export const RECORD_TYPE_LABELS: Record<RecordType, string> = {
  soap: 'SOAP notes',
  clinical_notes: 'Clinical notes',
  lab_results: 'Lab results',
  procedures: 'Procedures',
  vaccinations: 'Vaccinations',
  medications: 'Medications',
  imaging: 'Imaging',
  coggins: 'Coggins',
  attachments: 'Attachments',
};

export const SUMMARY_SECTIONS = [
  'active_problems',
  'current_medications',
  'alerts',
  'vaccine_due_dates',
  'coggins_status',
  'weight',
  'insurance',
] as const;
export type SummarySection = (typeof SUMMARY_SECTIONS)[number];

export const SUMMARY_LABELS: Record<SummarySection, string> = {
  active_problems: 'Active problems',
  current_medications: 'Current medications',
  alerts: 'Medical alerts',
  vaccine_due_dates: 'Vaccine due dates',
  coggins_status: 'Coggins status',
  weight: 'Weight',
  insurance: 'Insurance',
};

export type RangeKind = 'last_visit' | '3m' | '6m' | '12m' | 'all' | 'custom';
export type RecipientType = 'owner' | 'vet' | 'other';
export type ConsentMethod = 'verbal' | 'written' | 'signed_form';

export interface PurposePreset {
  range: RangeKind;
  types: RecordType[];
  summarySections: SummarySection[];
}

export interface EmailTemplate {
  subject: string;
  body: string;
}

export interface StableDocsSettings {
  consentText: string;
  footerText: string;
  emailDisclaimer: string;
  purposeTemplates: Record<Purpose, EmailTemplate>;
  presets: Record<Purpose, PurposePreset>;
  attachThresholdMb: number;
}

export interface Candidate {
  type: RecordType;
  id: string;
  date: string;
  title: string;
  subtitle: string | null;
  isDraft: boolean;
  version: string;
  sizeBytes: number | null;
}

export interface CandidateOwner {
  /** Null for an owner known only by name on the horse (no linked client record). */
  clientId: string | null;
  name: string;
  email: string | null;
  /** Every address on file for this owner (its own and its contacts'), lower-cased. */
  emails: string[];
}

export interface Candidates {
  items: Candidate[];
  counts: Record<string, number>;
  owners: CandidateOwner[];
}

export type ExportStatus = 'queued' | 'generating' | 'ready' | 'failed';

export interface ExportView {
  id: string;
  horseId: string;
  purpose: Purpose;
  status: ExportStatus;
  errorMessage: string | null;
  fileName: string | null;
  fileSizeBytes: number | null;
  pageCount: number | null;
  sha256: string | null;
  itemCount: number;
  createdAt: string;
  emailDraft: { subject: string; body: string; disclaimer: string } | null;
}

export interface CreateExportBody {
  purpose: Purpose;
  dateFrom?: string;
  dateTo?: string;
  items: { type: RecordType; id: string }[];
  includeAttachments?: boolean;
  includeDrafts?: boolean;
  summarySections?: SummarySection[];
  recipientName?: string;
}

export interface SendShareBody {
  recipientType: RecipientType;
  recipientEmail: string;
  recipientName?: string;
  ccEmails?: string[];
  subject?: string;
  body?: string;
  authorizingOwnerId?: string;
  consentAcknowledged?: boolean;
  consentMethod?: ConsentMethod;
  consentDocId?: string;
}

export type DeliveryStatus = 'sent' | 'delivered' | 'opened' | 'bounced' | 'failed';

export interface ShareRow {
  id: string;
  exportId: string;
  recipientType: RecipientType;
  recipientName: string | null;
  recipientEmail: string;
  ccEmails: string[];
  subject: string;
  deliveryMethod: string;
  deliveryStatus: DeliveryStatus;
  sentAt: string;
  senderName: string | null;
  purpose: Purpose;
  fileName: string | null;
  fileSizeBytes: number | null;
  itemCount: number;
}

export type SettingsUpdate = Partial<
  Pick<StableDocsSettings, 'consentText' | 'footerText' | 'emailDisclaimer' | 'purposeTemplates' | 'presets' | 'attachThresholdMb'>
>;

export const exportDownloadPath = (exportId: string) => `/stabledocs/exports/${exportId}/download`;

export const stableDocsService = {
  getCandidates: (horseId: string, includeDrafts: boolean): Promise<Candidates> =>
    api.get(`/stabledocs/horses/${horseId}/candidates`, { params: { includeDrafts } }).then((r) => r.data),

  createExport: (horseId: string, body: CreateExportBody): Promise<ExportView> =>
    api.post(`/stabledocs/horses/${horseId}/exports`, body).then((r) => r.data),

  getExport: (exportId: string): Promise<ExportView> =>
    api.get(`/stabledocs/exports/${exportId}`).then((r) => r.data),

  send: (exportId: string, body: SendShareBody): Promise<{ shareId: string; itemCount: number }> =>
    api.post(`/stabledocs/exports/${exportId}/shares`, body).then((r) => r.data),

  listShares: (horseId: string): Promise<ShareRow[]> =>
    api.get(`/stabledocs/horses/${horseId}/shares`).then((r) => r.data),

  getSettings: (): Promise<StableDocsSettings> => api.get('/stabledocs/settings').then((r) => r.data),

  updateSettings: (body: SettingsUpdate): Promise<StableDocsSettings> =>
    api.put('/stabledocs/settings', body).then((r) => r.data),

  /** The signed authorization form for an 'other' recipient; it lands in the patient's files. */
  uploadConsentForm: (horseId: string, file: File): Promise<{ id: string }> => {
    const form = new FormData();
    form.append('file', file);
    form.append('horseId', horseId);
    form.append('category', 'document');
    return api
      .post('/attachments/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
};
